import React, { useContext, useMemo, useState } from 'react';
import {
  Button,
  ButtonGroup,
  Checkbox,
  FormControl,
  Menu,
  MenuItem,
  Select,
  makeStyles,
} from '@material-ui/core';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import { NestedMenuItem } from '@mummssoftware/common-ui';
import { ALL } from '@mummssoftware/utils/constants';
import { VisitsGlobalContext } from '@mummssoftware/global-context';
import { isEqual } from 'lodash-es';
import {
  FilterHeader,
  FilterContainer,
} from '../../components/layout/WrapperDiv';
import { filterByAttribute } from '../../translations/en.json';
import { AppContext } from '../../context';
import {
  attributeNamesForIds,
  isAttributeFilterAllSelected,
  partitionAttributeFilterOptions,
  SUMMARY_ATTRIBUTE_MATCH_AND,
  SUMMARY_ATTRIBUTE_MATCH_OR,
  SummaryAttributeMatchMode,
  toggleAttributeFilterId,
  VisitAttributeFilterOption,
  visitAttributesToFilterOptions,
} from '../Context/visitSummaryUtils';
import { SummaryFilterContext } from './SummaryFilters';

const useStyles = makeStyles((theme) => ({
  select: (props: { changed: boolean }) => ({
    fontSize: '.85rem',
    width: '15ch',
    '&:before': {
      borderColor:
        (props.changed && `${(theme as any).palette.text.secondary}`) ||
        `${(theme as any).palette.text.primary}`,
    },
    '&:after': {
      borderColor: `${(theme as any).palette.text.secondary}`,
    },
  }),
  icon: (props: { changed: boolean }) => ({
    fill:
      (props.changed && `${(theme as any).palette.text.secondary}`) ||
      `${(theme as any).palette.text.primary}`,
  }),
  root: (props: { changed: boolean }) => ({
    color:
      (props.changed && `${(theme as any).palette.text.secondary}`) ||
      `${(theme as any).palette.text.primary}`,
  }),
  menu: {
    maxHeight: '50vh',
  },
  menuItem: {
    padding: 0,
    minHeight: 32,
  },
  menuRow: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 32,
    padding: '2px 16px 2px 8px',
  },
  menuGutter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    flexShrink: 0,
  },
  checkbox: {
    padding: 0,
  },
  checkboxLabel: {
    fontSize: '.85rem',
  },
  nestedMenuItem: {
    fontSize: '.85rem',
    padding: 0,
    minHeight: 32,
  },
  nestedLabel: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    minHeight: 32,
    padding: '2px 16px 2px 8px',
  },
  nestedArrow: {
    fontSize: 32,
  },
  menuHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '4px 8px',
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  matchToggleGroup: {
    boxShadow: 'none',
    backgroundColor: theme.palette.background.paper,
  },
  matchToggleButton: {
    fontSize: '0.7rem',
    lineHeight: 1.2,
    minWidth: 36,
    padding: '2px 8px',
    textTransform: 'none',
    color: theme.palette.text.secondary,
  },
  matchToggleButtonSelected: {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.selected,
    '&:hover': {
      backgroundColor: theme.palette.action.selected,
    },
  },
  matchToggleButtonDisabled: {
    color: theme.palette.text.disabled,
  },
}));

const AttributeFilterMatchModeHeader = ({
  matchMode,
  disabled,
  onChange,
  classes,
}: {
  matchMode: SummaryAttributeMatchMode;
  disabled: boolean;
  onChange: (mode: SummaryAttributeMatchMode) => void;
  classes: ReturnType<typeof useStyles>;
}) => (
  <div className={classes.menuHeader}>
    <ButtonGroup
      className={classes.matchToggleGroup}
      size="small"
      variant="text"
      disabled={disabled}
    >
      <Button
        className={`${classes.matchToggleButton} ${
          matchMode === SUMMARY_ATTRIBUTE_MATCH_AND
            ? classes.matchToggleButtonSelected
            : ''
        } ${disabled ? classes.matchToggleButtonDisabled : ''}`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onChange(SUMMARY_ATTRIBUTE_MATCH_AND);
        }}
      >
        AND
      </Button>
      <Button
        className={`${classes.matchToggleButton} ${
          matchMode === SUMMARY_ATTRIBUTE_MATCH_OR
            ? classes.matchToggleButtonSelected
            : ''
        } ${disabled ? classes.matchToggleButtonDisabled : ''}`}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onChange(SUMMARY_ATTRIBUTE_MATCH_OR);
        }}
      >
        OR
      </Button>
    </ButtonGroup>
  </div>
);

type AttributeFilterCheckboxItemProps = {
  label: string;
  checked: boolean;
  onToggle: () => void;
  classes: ReturnType<typeof useStyles>;
};

const AttributeFilterCheckboxItem = ({
  label,
  checked,
  onToggle,
  classes,
}: AttributeFilterCheckboxItemProps) => (
  <MenuItem
    className={classes.menuItem}
    dense
    onClick={(event) => {
      event.preventDefault();
      event.stopPropagation();
      onToggle();
    }}
  >
    <div className={classes.menuRow}>
      <span className={classes.menuGutter}>
        <Checkbox
          className={classes.checkbox}
          checked={checked}
          color="primary"
          size="small"
          tabIndex={-1}
          disableRipple
        />
      </span>
      <span className={classes.checkboxLabel}>{label}</span>
    </div>
  </MenuItem>
);

export const AttributeFilter = () => {
  const { visitAttributes } = useContext(AppContext);
  const { summaryAttributeIds, summaryAttributeMatchMode } = useContext(
    VisitsGlobalContext,
  );
  const { tentativeFilters, updateTentFilters } = useContext(
    SummaryFilterContext,
  );

  const attributesChanged =
    !isEqual(summaryAttributeIds, tentativeFilters.summaryAttributeIds) ||
    summaryAttributeMatchMode !== tentativeFilters.summaryAttributeMatchMode;

  const classes = useStyles({ changed: attributesChanged });

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const attributeOptions = useMemo(
    () => visitAttributesToFilterOptions(visitAttributes),
    [visitAttributes],
  );

  const { hopeAttributes, otherAttributes } = useMemo(
    () => partitionAttributeFilterOptions(attributeOptions),
    [attributeOptions],
  );

  const selectedIds = tentativeFilters.summaryAttributeIds || [];
  const matchMode =
    tentativeFilters.summaryAttributeMatchMode || SUMMARY_ATTRIBUTE_MATCH_AND;
  const allSelected = isAttributeFilterAllSelected(selectedIds);

  const setSelectedIds = (attributeIds: number[]) => {
    updateTentFilters({
      ...tentativeFilters,
      summaryAttributeIds: attributeIds,
    });
  };

  const setMatchMode = (nextMatchMode: SummaryAttributeMatchMode) => {
    updateTentFilters({
      ...tentativeFilters,
      summaryAttributeMatchMode: nextMatchMode,
    });
  };

  const toggleAttribute = (attributeId: number) => {
    setSelectedIds(toggleAttributeFilterId(selectedIds, attributeId));
  };

  const isAttributeSelected = (attributeId: number) =>
    selectedIds.includes(attributeId);

  const renderAttributeItem = (attribute: VisitAttributeFilterOption) => (
    <AttributeFilterCheckboxItem
      key={`summary-attribute-${attribute.id}`}
      classes={classes}
      label={attribute.name}
      checked={isAttributeSelected(attribute.id)}
      onToggle={() => toggleAttribute(attribute.id)}
    />
  );

  const displayValue = allSelected
    ? ALL
    : attributeNamesForIds(selectedIds, attributeOptions).join(', ');

  return (
    <FilterContainer className="visit-summary-attribute-filter">
      <FilterHeader selected={attributesChanged}>
        {filterByAttribute}
      </FilterHeader>
      {attributeOptions.length > 0 && (
        <div style={{ display: 'flex', paddingTop: '5px' }}>
          <FormControl style={{ paddingLeft: '3px', width: '15ch' }}>
            <Select
              id="visitSummaryAttributeFilter"
              open={false}
              value="attribute-filter"
              renderValue={() => displayValue}
              className={classes.select}
              onClick={(event) => setAnchorEl(event.currentTarget)}
              inputProps={{
                title: displayValue,
                'aria-controls': menuOpen
                  ? 'visit-summary-attribute-filter-menu'
                  : undefined,
                'aria-haspopup': true,
                'aria-expanded': menuOpen ? 'true' : undefined,
                classes: {
                  icon: classes.icon,
                  root: classes.root,
                },
              }}
            >
              <MenuItem value="attribute-filter" />
            </Select>
          </FormControl>
          <Menu
            id="visit-summary-attribute-filter-menu"
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            PaperProps={{ className: classes.menu }}
            MenuListProps={{
              'aria-labelledby': 'visitSummaryAttributeFilter',
            }}
          >
            <AttributeFilterMatchModeHeader
              classes={classes}
              disabled={allSelected}
              matchMode={matchMode}
              onChange={setMatchMode}
            />
            <AttributeFilterCheckboxItem
              classes={classes}
              label={ALL}
              checked={allSelected}
              onToggle={() => setSelectedIds([])}
            />
            {otherAttributes.map(renderAttributeItem)}
            {hopeAttributes.length > 0 && (
              <NestedMenuItem
                key="summary-attribute-hope"
                parentMenuOpen={menuOpen}
                className={classes.nestedMenuItem}
                rightIcon={null}
                label={
                  <span className={classes.nestedLabel}>
                    <span className={classes.menuGutter}>
                      <ArrowRightIcon className={classes.nestedArrow} />
                    </span>
                    HOPE
                  </span>
                }
              >
                {hopeAttributes.map(renderAttributeItem)}
              </NestedMenuItem>
            )}
          </Menu>
        </div>
      )}
    </FilterContainer>
  );
};
